import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import _get from 'lodash/get';
import { ColorEnum, Site } from 'graphql-common';
import DashboardContentMaxWidth from '@lib/layouts/DashboardLayout/DashboardContentMaxWidth/DashboardContentMaxWidth';
import Trans from '@lib/components/Trans/Trans';
import Button, {
  ButtonSizes,
  ButtonTypes,
} from '@lib/components/Button/Button';
import CircleLoader from '@lib/components/CircleLoader/CircleLoader';
import ConfirmationModal from '@lib/components/ConfirmationModal/ConfirmationModal';
import { materialSymbolsOutlined } from '@lib/enums/common';
import ErrorPage from '@lib/components/ErrorPage/ErrorPage';
import getStatusFromError from '@lib/utils/getStatusFromError';
import getDescriptionFromError from '@lib/utils/getDescriptionFromError';
import { IconName } from '@lib/components/Modal/enums';
import { UseSiteActionsResult } from 'routes/Sites/utils/useSiteActions';
import {
  Modals,
  UseModalManagementResult,
} from '@lib/hooks/useModalManagement';
import WhiteBox from '@lib/components/WhiteBox/WhiteBox';
import DashboardTitleHeader from '@lib/layouts/DashboardLayout/DashboardTitleHeader/DashboardTitleHeader';
import ViewOnlyChip from '@lib/components/ViewOnlyChip/ViewOnlyChip';
import entityViewStyles from '@lib/assets/styles/EntityView.module.scss';
import MaterialIcon from '@lib/components/MaterialIcon/MaterialIcon';
import Typography from '@lib/components/Typography/Typography';
import Chip from '@lib/components/Chip/Chip';
import { colorToChipType } from '@lib/components/Chip/utils/colorEnum';
import LinkComponent, { LinkTypes } from '@lib/components/Link/Link';
import TextEditor from '@lib/components/TextEditor/TextEditor';
import { hasTextEditorValue } from '@lib/components/TextEditor/utils';
import UserChip from '@lib/components/UserChip/UserChip';
import classNames from 'classnames';
import Map from '@lib/components/Map/Map';
import Image from '@lib/components/Image/Image';
import IconButton, {
  IconButtonTypes,
} from '@lib/components/IconButton/IconButton';
import {
  getFormattedDate,
  getLastTimeFromArray,
} from '@lib/utils/dateTimeHelpers';
import { APP_URLS } from 'constants/urls';
import { SitePointerShape } from '@lib/components/Map/enums';
import SiteMapPopupContent from 'components/SiteMapPopupContent/SiteMapPopupContent';
import { addWhiteToIconUrl } from '../utils/addWhiteToIconUrl';
import siteViewStyles from './SiteView.module.scss';

interface Props {
  isViewOnly: boolean;
  useSiteActionsResult: UseSiteActionsResult;
  useModalManagementResult: UseModalManagementResult;
}

function SiteView(props: Props) {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const { isViewOnly, useSiteActionsResult, useModalManagementResult } = props;
  const {
    destroyLoading,
    onConfirmedDelete,
    siteQuery,
    onEditCodeOfConduct,
    onOpenSectorPage,
    onViewCodeOfConduct,
  } = useSiteActionsResult;
  const siteData = _get(siteQuery, 'data.site') as Site;
  const {
    code,
    description,
    email,
    id,
    locationPhotoAttached,
    name,
    phoneNumber,
    sector,
    siteConductCodeSections = [],
    user,
    locationPoint,
  } = siteData || {};
  const locationGeojson = _get(locationPoint, ['geojson']);
  const locationGeojsonProperties = _get(locationGeojson, [
    'features',
    0,
    'properties',
  ]);
  const addressDetails = _get(locationGeojsonProperties, 'addressDetails', '');
  const shape = _get(locationGeojsonProperties, 'shape') as SitePointerShape;
  const color = _get(locationGeojsonProperties, 'color') as ColorEnum;
  const icon = _get(locationGeojsonProperties, 'icon');

  const siteConductCodeSectionsUpdatedAt = getFormattedDate(
    getLastTimeFromArray(
      siteConductCodeSections.map(({ updatedAt }) => updatedAt as string),
    ),
  );

  const locationPhotoUrl =
    locationPhotoAttached?.urls?.large || locationPhotoAttached?.urls?.original;

  const onEditHandler = () =>
    navigate(
      APP_URLS.dashboard.sites.edit.getDynamicPath({
        pathParts: {
          id: `${id}`,
        },
      }),
    );
  const { openModal, closeModal, isModalOpened } = useModalManagementResult;
  const openSiteDeleteModal = () => openModal(Modals.SiteDestroyModal);
  const closeSiteDeleteModal = () => closeModal(Modals.SiteDestroyModal);

  if (siteQuery.error) {
    return (
      <ErrorPage
        status={getStatusFromError(siteQuery.error)}
        description={getDescriptionFromError(siteQuery.error)}
      />
    );
  }

  const popupContentRender = (renderProps) => {
    return <SiteMapPopupContent site={siteData} {...renderProps} />;
  };

  return (
    <>
      {siteQuery.loading ? (
        <CircleLoader />
      ) : (
        <DashboardContentMaxWidth>
          <WhiteBox>
            <DashboardTitleHeader
              title={name}
              actions={
                isViewOnly ? (
                  <ViewOnlyChip key="view" />
                ) : (
                  <Button
                    buttonText={t('edit')}
                    buttonType={ButtonTypes.primaryFilled}
                    onClick={onEditHandler}
                    leftIcon="edit"
                    leftIconClassName={materialSymbolsOutlined}
                  />
                )
              }
            />
            <div className={entityViewStyles.infoList}>
              <div className={entityViewStyles.infoItem}>
                <p className={entityViewStyles.infoItemLabel}>
                  <MaterialIcon icon="share_location" symbolsOutlined />
                  <Typography variant="caption">{t('sector')}</Typography>
                </p>
                <div className={entityViewStyles.infoItemContent}>
                  {sector ? (
                    <Chip
                      label={sector.name}
                      leftIcon="circle"
                      onClick={onOpenSectorPage}
                      type={colorToChipType(sector.color)}
                    />
                  ) : (
                    <Typography variant="body">-</Typography>
                  )}
                </div>
              </div>
              <div className={entityViewStyles.infoItem}>
                <p className={entityViewStyles.infoItemLabel}>
                  <MaterialIcon icon="password" symbolsOutlined />
                  <Typography variant="caption">{t('site-code')}</Typography>
                </p>
                <div className={entityViewStyles.infoItemContent}>
                  <Typography variant="body">{code || t('no-code')}</Typography>
                </div>
              </div>
              <div className={entityViewStyles.infoItem}>
                <p className={entityViewStyles.infoItemLabel}>
                  <MaterialIcon icon="call" symbolsOutlined />
                  <Typography variant="caption">{t('phone-number')}</Typography>
                </p>
                <div className={entityViewStyles.infoItemContent}>
                  <Typography variant="body">{phoneNumber || '-'}</Typography>
                </div>
              </div>
              <div className={entityViewStyles.infoItem}>
                <p className={entityViewStyles.infoItemLabel}>
                  <MaterialIcon icon="mail" symbolsOutlined />
                  <Typography variant="caption">{t('email')}</Typography>
                </p>
                <div className={entityViewStyles.infoItemContent}>
                  <LinkComponent
                    type={LinkTypes.secondaryGray}
                    data-test-id="site-view__email-link"
                  >
                    {email}
                  </LinkComponent>
                </div>
              </div>
              {hasTextEditorValue(description) && (
                <div
                  className={classNames(
                    entityViewStyles.infoItem,
                    entityViewStyles.block,
                  )}
                >
                  <p className={entityViewStyles.infoItemLabel}>
                    <MaterialIcon icon="description" symbolsOutlined />
                    <Typography variant="caption">
                      {t('description')}
                    </Typography>
                  </p>
                  <div className={entityViewStyles.infoItemContent}>
                    <TextEditor
                      readOnly
                      value={description}
                      className={entityViewStyles.textEditor}
                    />
                  </div>
                </div>
              )}
            </div>
            <div className={entityViewStyles.infoList}>
              <Typography
                variant="h3"
                className={entityViewStyles.infoListTitle}
              >
                {t('responsible-person')}
              </Typography>
              <div className={entityViewStyles.infoItem}>
                <p className={entityViewStyles.infoItemLabel}>
                  <MaterialIcon icon="person" symbolsOutlined />
                  <Typography variant="caption">{t('name')}</Typography>
                </p>
                <div className={entityViewStyles.infoItemContent}>
                  {user ? (
                    <UserChip user={user} />
                  ) : (
                    <Typography variant="body">-</Typography>
                  )}
                </div>
              </div>
              <div className={entityViewStyles.infoItem}>
                <p className={entityViewStyles.infoItemLabel}>
                  <MaterialIcon icon="call" symbolsOutlined />
                  <Typography variant="caption">{t('phone-number')}</Typography>
                </p>
                <div className={entityViewStyles.infoItemContent}>
                  <Typography variant="body">
                    {user?.phoneNumber || '-'}
                  </Typography>
                </div>
              </div>
              <div className={entityViewStyles.infoItem}>
                <p className={entityViewStyles.infoItemLabel}>
                  <MaterialIcon icon="mail" symbolsOutlined />
                  <Typography variant="caption">{t('email')}</Typography>
                </p>
                <div className={entityViewStyles.infoItemContent}>
                  {user?.email ? (
                    <LinkComponent
                      type={LinkTypes.secondaryGray}
                      data-test-id="site-view__user-email-link"
                    >
                      {user.email}
                    </LinkComponent>
                  ) : (
                    <Typography variant="body">-</Typography>
                  )}
                </div>
              </div>
            </div>
            <div className={entityViewStyles.infoList}>
              <Typography
                variant="h3"
                className={entityViewStyles.infoListTitle}
              >
                {t('location-on-map')}
              </Typography>
              <div className={entityViewStyles.infoItem}>
                <p className={entityViewStyles.infoItemLabel}>
                  <MaterialIcon icon="distance" symbolsOutlined />
                  <Typography variant="caption">
                    {t('site-address-details')}
                  </Typography>
                </p>
                <div className={entityViewStyles.infoItemContent}>
                  <Typography variant="body">
                    {addressDetails || '-'}
                  </Typography>
                </div>
              </div>
              <div
                className={classNames(
                  entityViewStyles.infoItem,
                  entityViewStyles.block,
                )}
              >
                <Map
                  value={locationGeojson}
                  PopupContent={popupContentRender}
                  pointerProps={{
                    address: null,
                    id: null,
                    pointCount: null,
                    sourceables: [],
                    type: null,
                    addressDetails,
                    shape: shape || undefined,
                    color: color || undefined,
                    icon: icon ? addWhiteToIconUrl(icon) : '',
                  }}
                />
              </div>
            </div>
            {locationPhotoUrl && (
              <div className={siteViewStyles.locationPhoto}>
                <Image src={locationPhotoUrl} alt={name} />
              </div>
            )}
            {siteConductCodeSections.length > 0 && (
              <div className={siteViewStyles.conductCodeSections}>
                <div className={siteViewStyles.conductCodeSectionsIcon}>
                  <MaterialIcon icon="attach_file" />
                </div>
                <div>
                  <Typography variant="body">{t('code-of-conduct')}</Typography>
                  <Typography
                    variant="caption"
                    className={siteViewStyles.conductCodeSectionsDate}
                  >
                    {t('updated-on')} {siteConductCodeSectionsUpdatedAt}
                  </Typography>
                </div>
                <div className={siteViewStyles.conductCodeSectionsActions}>
                  <IconButton
                    icon="visibility"
                    onClick={onViewCodeOfConduct}
                    symbolsOutlined
                    type={IconButtonTypes.PrimaryOutlined}
                  />
                  {!isViewOnly && (
                    <IconButton
                      icon="edit"
                      onClick={onEditCodeOfConduct}
                      symbolsOutlined
                      type={IconButtonTypes.PrimaryOutlined}
                    />
                  )}
                </div>
              </div>
            )}
            {siteConductCodeSections.length === 0 && !isViewOnly && (
              <div className={siteViewStyles.conductCodeAddBtn}>
                <Button
                  buttonText={t('add-code-of-conduct')}
                  buttonType={ButtonTypes.primaryOutlined}
                  buttonSize={ButtonSizes.small}
                  leftIcon="add"
                  onClick={onEditCodeOfConduct}
                />
              </div>
            )}
          </WhiteBox>
          {!isViewOnly && (
            <div className={entityViewStyles.actions}>
              <Button
                buttonText={t('delete-site')}
                buttonType={ButtonTypes.negativeOutlined}
                leftIcon="delete"
                leftIconClassName={materialSymbolsOutlined}
                onClick={openSiteDeleteModal}
              />
            </div>
          )}
        </DashboardContentMaxWidth>
      )}
      <ConfirmationModal
        confirmButtonLabel={t('yes-delete')}
        description={<Trans i18nKey="delete-site-text" />}
        icon={IconName.Delete}
        isOpen={isModalOpened(Modals.SiteDestroyModal)}
        loading={destroyLoading}
        onClose={closeSiteDeleteModal}
        onConfirm={onConfirmedDelete}
        title={t('are-you-sure')}
        useProgress
      />
    </>
  );
}

export default SiteView;
